/** Sterowanie orientacją ekranu: pion wszędzie poza grafikiem. */

import { useEffect } from 'react'

/**
 * `lock()` istnieje w przeglądarkach mobilnych, ale nie ma go w typach DOM
 * tej wersji TypeScriptu — stąd własne rozszerzenie zamiast `any`.
 */
type OrientacjaZBlokada = ScreenOrientation & {
  lock?: (orientation: 'portrait-primary') => Promise<void>
}

/**
 * Ekrany, na których wolno obrócić telefon.
 *
 * Siatka grafiku ma ponad 1000 px szerokości — na ekranie 390 px widać z niej
 * jedną trzecią, poziomo prawie trzy czwarte (zmierzone). Reszta aplikacji to
 * listy i formularze, które w pionie czyta się wygodniej, a przypadkowy obrót
 * w kieszeni tylko przeszkadza.
 */
const OBROT_DOZWOLONY = [
  /^\/grafik(\/|$)/,
  // Grafik sali, ale nie sama lista sal.
  /^\/sale\/[^/]+$/,
]

export function czyObrotDozwolony(sciezka: string): boolean {
  return OBROT_DOZWOLONY.some((wzorzec) => wzorzec.test(sciezka))
}

/**
 * Trzyma pion wszędzie poza grafikiem.
 *
 * Blokada siedzi tutaj, a nie w manifeście, z powodu, który kosztował jedno
 * wdrożenie: Chrome przy instalacji na Androidzie zapisuje `orientation`
 * z manifestu jako blokadę aktywności Androida. `unlock()` z JavaScriptu jej
 * nie zdejmuje, więc grafik nie obracał się mimo zwolnionej blokady. Manifest
 * mówi teraz `any`, a pionu pilnuje aplikacja — tego da się dopilnować
 * z poziomu strony.
 *
 * Jedna funkcja dla całej aplikacji, wołana z `AppLayout` i zależna od adresu.
 * Rozbicie na „zablokuj” w layoucie i „zwolnij” na podstronie nie zadziała:
 * React uruchamia efekty od dziecka do rodzica, więc wejście prosto na
 * `/grafik` kończyłoby się zwolnieniem blokady, a zaraz potem jej założeniem.
 */
export function useOrientationPolicy(sciezka: string): void {
  useEffect(() => {
    const orientation = window.screen?.orientation as OrientacjaZBlokada | undefined
    if (!orientation || typeof orientation.lock !== 'function') return

    if (czyObrotDozwolony(sciezka)) {
      orientation.unlock?.()
      return
    }

    // Odmowa to zwykły brak wsparcia — w karcie przeglądarki blokada orientacji
    // nie działa i nie ma się co dziwić. Wtedy telefon obraca się swobodnie.
    void orientation.lock('portrait-primary').catch(() => {})
  }, [sciezka])
}
